import { useState, useEffect } from 'react';
import type { DailyLog } from './types';
import apiClient from '@/lib/api/client';

interface DailyLogFormProps {
  jobId: string;
  log?: DailyLog | null;
  onSave: () => void;
  onCancel: () => void;
  onError?: (msg: string) => void;
}

export const DailyLogForm = ({ jobId, log, onSave, onCancel, onError }: DailyLogFormProps) => {
  const [formData, setFormData] = useState({
    log_date: new Date().toISOString().split('T')[0],
    weather: 'Clear',
    temperature: 15,
    crew_size: 1,
    hours_worked: 8,
    work_performed: '',
    materials_used: '',
    equipment_used: '',
    visitors: '',
    safety_incidents: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (log) {
      setFormData({
        log_date: log.log_date.split('T')[0],
        weather: log.weather,
        temperature: log.temperature,
        crew_size: log.crew_size,
        hours_worked: log.hours_worked,
        work_performed: log.work_performed,
        materials_used: log.materials_used || '',
        equipment_used: log.equipment_used || '',
        visitors: log.visitors || '',
        safety_incidents: log.safety_incidents || '',
        notes: log.notes || '',
      });
    }
  }, [log]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.work_performed.trim()) {
      onError?.('Please describe the work performed.');
      return;
    }

    try {
      setSaving(true);
      if (log) {
        await apiClient.put(`/jobs/${jobId}/daily-logs/${log.id}`, formData);
      } else {
        await apiClient.post(`/jobs/${jobId}/daily-logs`, formData);
      }
      onSave();
    } catch (error: unknown) {
      const axiosErr = error as { response?: { data?: { message?: string } } };
      onError?.(axiosErr?.response?.data?.message || 'Failed to save daily log.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">
        {log ? 'Edit Daily Log' : 'Add Daily Log'}
      </h3>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Date and Conditions Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date *
            </label>
            <input
              type="date"
              required
              value={formData.log_date}
              onChange={(e) => setFormData({ ...formData, log_date: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Weather
            </label>
            <select
              value={formData.weather}
              onChange={(e) => setFormData({ ...formData, weather: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="Clear">Clear</option>
              <option value="Partly Cloudy">Partly Cloudy</option>
              <option value="Cloudy">Cloudy</option>
              <option value="Rain">Rain</option>
              <option value="Heavy Rain">Heavy Rain</option>
              <option value="Snow">Snow</option>
              <option value="Fog">Fog</option>
              <option value="Windy">Windy</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Temperature (°C)
            </label>
            <input
              type="number"
              value={formData.temperature}
              onChange={(e) => setFormData({ ...formData, temperature: parseInt(e.target.value) })}
              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Crew Size
            </label>
            <input
              type="number"
              min="1"
              value={formData.crew_size}
              onChange={(e) => setFormData({ ...formData, crew_size: parseInt(e.target.value) })}
              className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hours Worked
          </label>
          <input
            type="number"
            step="0.5"
            min="0"
            value={formData.hours_worked}
            onChange={(e) => setFormData({ ...formData, hours_worked: parseFloat(e.target.value) })}
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Work Performed */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Work Performed *
          </label>
          <textarea
            required
            rows={4}
            value={formData.work_performed}
            onChange={(e) => setFormData({ ...formData, work_performed: e.target.value })}
            placeholder="Describe the work completed today..."
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          />
        </div>

        {/* Materials Used */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Materials Used
          </label>
          <textarea
            rows={2}
            value={formData.materials_used}
            onChange={(e) => setFormData({ ...formData, materials_used: e.target.value })}
            placeholder="List materials used (optional)..."
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          />
        </div>

        {/* Equipment Used */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Equipment Used
          </label>
          <textarea
            rows={2}
            value={formData.equipment_used}
            onChange={(e) => setFormData({ ...formData, equipment_used: e.target.value })}
            placeholder="List equipment used (optional)..."
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          />
        </div>

        {/* Visitors */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Visitors
          </label>
          <input
            type="text"
            value={formData.visitors}
            onChange={(e) => setFormData({ ...formData, visitors: e.target.value })}
            placeholder="Inspectors, clients, subcontractors..."
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Safety Incidents */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Safety Incidents
          </label>
          <textarea
            rows={2}
            value={formData.safety_incidents}
            onChange={(e) => setFormData({ ...formData, safety_incidents: e.target.value })}
            placeholder="Document any safety incidents or concerns..."
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          />
        </div>

        {/* Additional Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes
          </label>
          <textarea
            rows={3}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Any additional notes or observations..."
            className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : log ? 'Update Log' : 'Save Log'}
          </button>
        </div>
      </form>
    </div>
  );
};


